import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  List,
  Typography,
  Space,
  Tag,
  Modal,
  message,
  Spin,
  Alert,
  Divider,
  Row,
  Col,
} from 'antd';
import {
  CreditCardOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import apiClient from '../utils/axios';
import { useAuth0 } from '@auth0/auth0-react';

const { Title, Text } = Typography;

// Load Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  is_default: boolean;
  created: number;
}

interface PaymentMethodManagerProps {
  organizationId: string;
  onPaymentMethodsChange?: () => void;
}

// Component for adding new payment method
const AddPaymentMethodForm: React.FC<{
  organizationId: string;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ organizationId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const token = await getAccessTokenSilently();
      
      // Create setup intent
      const setupIntentResponse = await apiClient.post(
        `/api/organization/${organizationId}/stripe/setup-intent`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { client_secret } = setupIntentResponse.data;

      // Confirm setup intent with payment method
      const { error, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
        },
      });

      if (error) {
        message.error(error.message || 'Failed to add payment method');
      } else if (setupIntent?.status === 'succeeded') {
        message.success('Payment method added successfully');
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error adding payment method:', error);
      message.error(error.response?.data?.detail || 'Failed to add payment method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 16 }}>
        <Text strong>Add New Payment Method</Text>
      </div>
      
      <div style={{ 
        padding: '12px', 
        border: '1px solid #d9d9d9', 
        borderRadius: '6px',
        marginBottom: 16 
      }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
            },
          }}
        />
      </div>

      <Space>
        <Button 
          type="primary" 
          htmlType="submit" 
          loading={loading}
          disabled={!stripe}
        >
          Add Payment Method
        </Button>
        <Button onClick={onCancel}>
          Cancel
        </Button>
      </Space>
    </form>
  );
};

// Main payment method manager component
const PaymentMethodManagerContent: React.FC<PaymentMethodManagerProps> = ({
  organizationId,
  onPaymentMethodsChange,
}) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get(
        `/api/organization/${organizationId}/stripe/payment-methods`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error: any) {
      console.error('Error fetching payment methods:', error);
      if (error.response?.status !== 404) {
        message.error('Failed to load payment methods');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, [organizationId]);

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      setActionLoading(paymentMethodId);
      const token = await getAccessTokenSilently();
      
      await apiClient.post(
        `/api/organization/${organizationId}/stripe/payment-methods/${paymentMethodId}/set-default`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      message.success('Default payment method updated');
      await fetchPaymentMethods();
      onPaymentMethodsChange?.();
    } catch (error: any) {
      console.error('Error setting default payment method:', error);
      message.error(error.response?.data?.detail || 'Failed to update default payment method');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (paymentMethodId: string) => {
    Modal.confirm({
      title: 'Delete Payment Method',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this payment method?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          setActionLoading(paymentMethodId);
          const token = await getAccessTokenSilently();
          
          await apiClient.delete(
            `/api/organization/${organizationId}/stripe/payment-methods/${paymentMethodId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          
          message.success('Payment method deleted');
          await fetchPaymentMethods();
          onPaymentMethodsChange?.();
        } catch (error: any) {
          console.error('Error deleting payment method:', error);
          message.error(error.response?.data?.detail || 'Failed to delete payment method');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleAddSuccess = async () => {
    setShowAddForm(false);
    await fetchPaymentMethods();
    onPaymentMethodsChange?.();
  };

  const getCardBrandIcon = (brand: string) => {
    // You can add more specific brand icons here
    return <CreditCardOutlined />;
  };

  const formatCardInfo = (card: PaymentMethod['card']) => {
    if (!card) return 'Unknown';
    return `•••• •••• •••• ${card.last4} (${card.exp_month}/${card.exp_year})`;
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <CreditCardOutlined />
          Payment Methods
        </Space>
      }
      extra={
        !showAddForm && (
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setShowAddForm(true)}
          >
            Add Payment Method
          </Button>
        )
      }
    >
      {showAddForm && (
        <>
          <AddPaymentMethodForm
            organizationId={organizationId}
            onSuccess={handleAddSuccess}
            onCancel={() => setShowAddForm(false)}
          />
          <Divider />
        </>
      )}

      {paymentMethods.length === 0 ? (
        <Alert
          message="No Payment Methods"
          description="Add a payment method to manage your subscription billing."
          type="info"
          showIcon
        />
      ) : (
        <List
          dataSource={paymentMethods}
          renderItem={(method) => (
            <List.Item
              actions={[
                !method.is_default && (
                  <Button
                    type="link"
                    size="small"
                    loading={actionLoading === method.id}
                    onClick={() => handleSetDefault(method.id)}
                  >
                    Set as Default
                  </Button>
                ),
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={actionLoading === method.id}
                  onClick={() => handleDelete(method.id)}
                  disabled={method.is_default && paymentMethods.length === 1}
                >
                  Delete
                </Button>,
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={getCardBrandIcon(method.card?.brand || 'unknown')}
                title={
                  <Space>
                    <Text strong>
                      {method.card?.brand?.toUpperCase() || 'Card'}
                    </Text>
                    {method.is_default && (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        Default
                      </Tag>
                    )}
                  </Space>
                }
                description={formatCardInfo(method.card)}
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};

// Wrapper component with Stripe Elements provider
export const PaymentMethodManager: React.FC<PaymentMethodManagerProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentMethodManagerContent {...props} />
    </Elements>
  );
}; 